import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, catchError, map, of } from 'rxjs';

import { CVA_CONSTANTS } from '../../util/constants';
import { environment } from '../../../environments/environment';
import { API_ROUTES } from '../../util/route.constants';
import { PatchIPreference, PreferencesResponse } from 'app/models/preferences.interface';
import { ClassificationEnum, DefaultPreferences } from 'app/util/user-config.constants';

@Injectable({
  providedIn: 'root'
})
export class PreferencesService {
  private cachedPreference: PreferencesResponse | null = null;

  constructor(private readonly http: HttpClient) { }

  getPreferences(
    referenceId: string,
    preferenceType: string,
    refresh: boolean = false
  ): Observable<PreferencesResponse> {
    // Return cached value if it exists and refresh is not requested
    if (!refresh && this.cachedPreference) {
      return of(this.cachedPreference);
    }

    const params = {
      referenceId,
      preferenceType
    };
    return this.http.get<{ response: PreferencesResponse[] }>(
      environment.cva_url + API_ROUTES.USER_SETTING, { params }
    ).pipe(
      map(response => {
        const item = response.response[0];

        const preference: PreferencesResponse =
          Object.keys(item.preference || {}).length === 0
            ? {
              preference: {
                ...DefaultPreferences,
              },
              systemTemplateAccessList: item.systemTemplateAccessList
            }
            : item
        // Cache the fetched preference
        if (!preference.preference.classificationType) {
          preference.preference.classificationType = ClassificationEnum.ICD;
        }
        this.setPreference(preference);
        return preference
      }),
      catchError(() => {
        // On error, return default preference and cache it
        const defaultPreference = { preference: DefaultPreferences };
        this.setPreference(defaultPreference);
        return of(defaultPreference);
      })
    )
  }

  patchPreference(
    preference: PatchIPreference,
    practitionerId: string,
    uid?: string
  ): Observable<PreferencesResponse> {
    if (!uid) {
      return this.savePreferences(
        preference,
        practitionerId,
      ).pipe(
        map(response => {
          // Update the cache
          this.setPreference(response.response);
          return response.response;
        }),
        catchError((err) => {
          console.error("Error saving preferences:", err);
          throw err;
        })
      )
    } else {
      return this.updatePreferences(
        uid,
        preference
      ).pipe(
        map(response => {
          // Update the cache
          this.setPreference(response.response);
          return response.response;
        }),
        catchError((err) => {
          console.error("Error updating preferences:", err);
          throw err;
        })
      )
    }
  }

  savePreferences(
    preference: PatchIPreference,
    practitionerId: string,
  ): Observable<{ response: PreferencesResponse }> {
    const payload = {
      preference: preference,
      preferenceType: CVA_CONSTANTS.CONST.USER_PREFERENCES,
      referenceId: practitionerId
    }
    return this.http.post<{ response: PreferencesResponse }>(
      environment.cva_url + API_ROUTES.USER_SETTING,
      payload
    );
  }

  updatePreferences(
    uid: string,
    preference: PatchIPreference
  ): Observable<{ response: PreferencesResponse }> {
    const params = { uid };
    return this.http.patch<{ response: PreferencesResponse }>(
      environment.cva_url + API_ROUTES.USER_SETTING,
      preference,
      { params })
  }

  setPreference(preference: PreferencesResponse): void {
    this.cachedPreference = preference;
  }

  removePreference(): void {
    this.cachedPreference = null;
  }

  clearPreferences(): void {
    this.removePreference();
  }


}
